import React, { useContext, useState, useEffect, useMemo } from "react";
import "../SalesOrder.css";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Popconfirm,
  notification,
  Modal,
  Switch,
  Select,
  Input,
  InputNumber,
  Divider,
  Row,
  Col,
  Typography,
  Space,
  Alert,
  Pagination,
  Spin,
  Table,
  Tag,
  message,
  DatePicker,
  Collapse,
  Tooltip,
} from "antd";
import "react-quill/dist/quill.snow.css";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";
import mainContext from "../context/MainContext/mainContext";
import generatePurchaseOrderPDF from "../../../../../utils/generatePurchaseOrderPDF";
import GeneratePerformanceInvoicePDF from "../../../../../utils/generatePerformanceInvoicePDF";
import dayjs from "dayjs";
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

/** Rupees to 2 decimals — stable comparisons for billing */
const roundMoney = (v) => Math.round(Number(v || 0) * 100) / 100;

/** Credit note API dates: always `YYYY-MM-DDTHH:mm:ss.sssZ` at UTC midnight */
const toCreditNoteApiDate = (value) => {
  if (value == null || value === "") return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return `${d.format("YYYY-MM-DD")}T00:00:00.000Z`;
};

/**
 * Coerce InputNumber / manual entry to a number (handles strings, commas, ₹).
 * Manual entry often differs from "Distribute evenly" which sets plain numbers.
 */
const normalizeAmount = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return roundMoney(value);
  }
  const s = String(value)
    .replace(/[\u20b9₹]/g, "")
    .replace(/,/g, "")
    .trim();
  if (s === "" || s === "-" || s === ".") return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? roundMoney(n) : 0;
};

const { Title, Text } = Typography;
const { Option } = Select;

const List = ({ order, type }) => {
  const {
    id,
    setJustView,
    archiveSalesOrder,
    fetchSalesOrders,
    createBill,
    fetchAllParties,
    allParties,
  } = useContext(mainContext);
  const navigate = useNavigate();
  const [invoiceId, setInvoiceId] = useState(null);
  const [creditNoteCharges, setCreditNoteCharges] = useState({ printingCharge: 0, mountingCharge: 0 });

  const navigateToDetails = (salesOrderId, viewOnly) => {
    if (!salesOrderId) return;
    setJustView(Boolean(viewOnly));
    const qs = window.location.search || "";
    navigate(`/sales-order/${salesOrderId}${qs}`);
  };
  const fetchInvoicDataForCN = async (invoiceId) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/fetchInfoForCN?invoiceId=${invoiceId}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        }
      );
      const data = await response.json();

      if (data.success && data.invoice) {
        // Prefill printingCharge and mountingCharge from API response
        setCreditNoteCharges({
          printingCharge: data.invoice.printingCharge || 0,
          mountingCharge: data.invoice.mountingCharge || 0,
        });
        // Calculate totalAmount by summing inventory amounts
        const totalAmountFromAPI = Array.isArray(data.invoice.inventories)
          ? data.invoice.inventories.reduce((sum, inv) => sum + (inv.amount || 0), 0)
          : 0;
        setCreditNoteAmount(totalAmountFromAPI);
        // Transform the invoice data into the format expected by the credit note table
        const transformedData = data.invoice.inventories.map((inv, index) => ({
          key: (index + 1).toString(),
          codeNo: inv.invId.CodeNo || "-",
          location: inv.invId.locations?.name || "-",
          size: `${inv.invId.width || 0} x ${inv.invId.height || 0}`,
          startDate: inv.startDate ? toCreditNoteApiDate(inv.startDate) : null,
          endDate: inv.endDate ? toCreditNoteApiDate(inv.endDate) : null,
          amount: inv.amount || 0,
          creditNoteAmount: null,
          invId: inv.invId._id,
        }));
        setCreditNoteTableData(transformedData);
      } else {
        notification.warning({
          message: "No Data Found",
          description: data.message || "No invoice data found",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch invoice data",
      });
    }
  };
  const totalAmount =
    (order?.bdfId?.inventories?.reduce(
      (acc, inventory) =>
        acc +
        (inventory?.specialRate || 0) +
        (inventory?.printingCharge || 0) +
        (inventory?.mountingCharge || 0),
      0
    ) || 0) +
    (order?.bdfId?.suffixInventories?.reduce(
      (acc, inventory) => acc + (inventory?.specialRate || 0),
      0
    ) || 0);

  const splitBillTotals = (() => {
    const fromInventories =
      order?.bdfId?.inventories?.reduce(
        (acc, inv) => ({
          specialRate: acc.specialRate + (inv?.specialRate || 0),
          printingCharge: acc.printingCharge + (inv?.printingCharge || 0),
          mountingCharge: acc.mountingCharge + (inv?.mountingCharge || 0),
        }),
        { specialRate: 0, printingCharge: 0, mountingCharge: 0 }
      ) || { specialRate: 0, printingCharge: 0, mountingCharge: 0 };
    const suffixSpecial =
      order?.bdfId?.suffixInventories?.reduce(
        (acc, inv) => acc + (inv?.specialRate || 0),
        0
      ) || 0;
    return {
      specialRate: fromInventories.specialRate + suffixSpecial,
      printingCharge: fromInventories.printingCharge,
      mountingCharge: fromInventories.mountingCharge,
    };
  })();

  /**
   * Line keys must be unique and stable. Split API sends per line only invId + dueAmount
   * (invId falls back to BDF line id for suffix rows when product invId is absent).
   */
  const splitInventoryLines = useMemo(() => {
    const lines = [];
    (order?.bdfId?.inventories || []).forEach((inv, idx) => {
      const key = `main-${idx}`;
      lines.push({
        key,
        invLineId: inv._id,
        invId: inv.invId?._id,
        codeNo: inv.invId?.CodeNo ?? "-",
        locationName: inv.invId?.locations?.name ?? "-",
        lineSpecialTotal: Number(inv.specialRate) || 0,
      });
    });
    (order?.bdfId?.suffixInventories || []).forEach((inv, idx) => {
      const key = `suffix-${idx}`;
      lines.push({
        key,
        invLineId: inv._id,
        invId: null,
        codeNo: inv.suffixCode || "-",
        locationName: inv.vendor || "-",
        lineSpecialTotal: Number(inv.specialRate) || 0,
      });
    });
    return lines;
  }, [order?.bdfId?.inventories, order?.bdfId?.suffixInventories]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewBillsModalOpen, setViewBillsModalOpen] = useState(false);
  const [viewCnInfoModalOpen, setViewCnInfoModalOpen] = useState(false);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [splitBill, setSplitBill] = useState(false);
  const [selectedParties, setSelectedParties] = useState([]);
  /** Per party: printingCharge, mountingCharge (split bill only); special is sum of inv due amounts */
  const [partySplitFields, setPartySplitFields] = useState({});
  /** Per party, per inventory line key: dueAmount toward special rate */
  const [partyInvDueAmounts, setPartyInvDueAmounts] = useState({});
  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);
  const [selectedBillNumber, setSelectedBillNumber] = useState(null);
  const [creditNoteAmount, setCreditNoteAmount] = useState(0);
  const [billNumbers, setBillNumbers] = useState([]);
  const [loadingBillNumbers, setLoadingBillNumbers] = useState(false);
  const [isCreditNoteSubmitting, setIsCreditNoteSubmitting] = useState(false);
  const token = window.localStorage.getItem("token");
  const [creditNoteTableData, setCreditNoteTableData] = useState([]);
  // Amount below the table: sum of all Credit Note Amounts
  const creditNoteAmountSum = creditNoteTableData.reduce(
    (sum, row) => sum + (row.creditNoteAmount || 0),
    0
  );
  // Total = Amount + Printing + Mounting
  const creditNoteTotal = creditNoteAmountSum + (creditNoteCharges?.printingCharge || 0) + (creditNoteCharges?.mountingCharge || 0);
  // Remaining = Total - distributed Credit Note Amounts
  const remainingToDistribute = creditNoteTotal - creditNoteAmountSum;

  const [generateFullInvoice, setGenerateFullInvoice] = useState(false);
  const [isCreatingBill, setIsCreatingBill] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    if (splitBill && isBillingModalOpen) {
      fetchAllParties();
    }
  }, [splitBill, isBillingModalOpen]);

  useEffect(() => {
    setPartySplitFields((prev) => {
      const next = {};
      selectedParties.forEach((partyId) => {
        const pid = String(partyId);
        next[pid] = prev[pid] || {
          printingCharge: 0,
          mountingCharge: 0,
        };
      });
      return next;
    });
    setPartyInvDueAmounts((prev) => {
      const next = {};
      selectedParties.forEach((partyId) => {
        const pid = String(partyId);
        const prevParty = prev[pid] || {};
        const row = {};
        splitInventoryLines.forEach((line) => {
          row[line.key] = normalizeAmount(prevParty[line.key]);
        });
        next[pid] = row;
      });
      return next;
    });
  }, [selectedParties, splitInventoryLines]);

  const MONEY_EPS = 0.01;

  const partySpecialRateRaw = (partyId) =>
    splitInventoryLines.reduce(
      (sum, line) =>
        sum +
        normalizeAmount(partyInvDueAmounts[String(partyId)]?.[line.key]),
      0
    );

  /** Rounded total per party (for labels) */
  const partySpecialRate = (partyId) => roundMoney(partySpecialRateRaw(partyId));

  const allocatedSplit = selectedParties.reduce(
    (acc, partyId) => {
      const pid = String(partyId);
      const f = partySplitFields[pid] || {};
      return {
        specialRate: acc.specialRate + partySpecialRateRaw(pid),
        printingCharge: acc.printingCharge + normalizeAmount(f.printingCharge),
        mountingCharge: acc.mountingCharge + normalizeAmount(f.mountingCharge),
      };
    },
    { specialRate: 0, printingCharge: 0, mountingCharge: 0 }
  );
  const remainingSpecialRate = roundMoney(
    splitBillTotals.specialRate - allocatedSplit.specialRate
  );
  const remainingPrintingCharge = roundMoney(
    splitBillTotals.printingCharge - allocatedSplit.printingCharge
  );
  const remainingMountingCharge = roundMoney(
    splitBillTotals.mountingCharge - allocatedSplit.mountingCharge
  );

  /** Totals fully allocated — this is what enables Confirm (per-line checks can false-negative after manual entry). */
  const aggregateSplitBalanced =
    Math.abs(remainingSpecialRate) < MONEY_EPS &&
    Math.abs(remainingPrintingCharge) < MONEY_EPS &&
    Math.abs(remainingMountingCharge) < MONEY_EPS;

  const isSplitAllocationBalanced =
    splitBill &&
    selectedParties.length > 0 &&
    aggregateSplitBalanced;

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleOpenBillingModal = () => {
    setIsBillingModalOpen(true);
    setSelectedParties([]);
    setPartySplitFields({});
    setPartyInvDueAmounts({});
  };

  const handleCloseBillingModal = () => {
    setIsBillingModalOpen(false);
    setSplitBill(false); // Reset the switch when closing
    setSelectedParties([]);
    setPartySplitFields({});
    setPartyInvDueAmounts({});
    setIsCreatingBill(false); // Reset loading state
  };

  const distributeDimensionEvenly = (total) => {
    const n = selectedParties.length;
    if (n === 0) return {};
    const perParty = Math.round((total / n) * 100) / 100;
    const amounts = {};
    selectedParties.forEach((partyId, index) => {
      const pid = String(partyId);
      if (index === n - 1) {
        const sumOthers = selectedParties
          .slice(0, -1)
          .reduce((sum) => sum + perParty, 0);
        amounts[pid] = Math.round((total - sumOthers) * 100) / 100;
      } else {
        amounts[pid] = perParty;
      }
    });
    return amounts;
  };

  const handleSplitFieldChange = (partyId, field, value) => {
    const pid = String(partyId);
    const num = normalizeAmount(value);
    setPartySplitFields((prev) => ({
      ...prev,
      [pid]: {
        printingCharge: 0,
        mountingCharge: 0,
        ...prev[pid],
        [field]: num,
      },
    }));
  };

  const handleInvDueChange = (partyId, lineKey, value) => {
    const pid = String(partyId);
    const num = normalizeAmount(value);
    setPartyInvDueAmounts((prev) => ({
      ...prev,
      [pid]: {
        ...prev[pid],
        [lineKey]: num,
      },
    }));
  };

  const distributeBillEvenly = () => {
    if (selectedParties.length === 0) return;
    const invDue = {};
    selectedParties.forEach((partyId) => {
      invDue[String(partyId)] = {};
    });
    splitInventoryLines.forEach((line) => {
      const perParty = distributeDimensionEvenly(line.lineSpecialTotal);
      selectedParties.forEach((partyId) => {
        const pid = String(partyId);
        invDue[pid][line.key] = perParty[pid] || 0;
      });
    });
    setPartyInvDueAmounts(invDue);
    const printingByParty = distributeDimensionEvenly(
      splitBillTotals.printingCharge
    );
    const mountingByParty = distributeDimensionEvenly(
      splitBillTotals.mountingCharge
    );
    const merged = {};
    selectedParties.forEach((partyId) => {
      const pid = String(partyId);
      merged[pid] = {
        printingCharge: printingByParty[pid] || 0,
        mountingCharge: mountingByParty[pid] || 0,
      };
    });
    setPartySplitFields(merged);
  };

  const handleArchive = async (reason) => {
    try {
      await archiveSalesOrder(order?._id, reason);
      setDeleteModalOpen(false);
      setDeleteReason("");
      fetchSalesOrders(type);
    } catch (error) {
      notification.error({ message: "Error while archiving sales order" });
    }
  };

  const handleCreateBill = async () => {
    if (!order?._id) {
      notification.warning({ message: "Invalid Sales Order ID" });
      return;
    }

    setIsCreatingBill(true);

    try {
      // If splitBill is true, we loop through each selected party and call the API with the amount
      if (splitBill && selectedParties.length > 0) {
        let notificationMessage = "";
        // Loop through each selected party and call createBill for each party
        for (const partyId of selectedParties) {
          const pid = String(partyId);
          const fields = partySplitFields[pid] || {};
          const duePrintingAmount = normalizeAmount(fields.printingCharge);
          const dueMountingAmount = normalizeAmount(fields.mountingCharge);
          const specialForParty = partySpecialRate(pid);
          const subTotal =
            specialForParty + duePrintingAmount + dueMountingAmount;
          const dueAmountInvWise = splitInventoryLines.map((line) => ({
            invId: line.invId ?? line.invLineId ?? null,
            dueAmount: normalizeAmount(
              partyInvDueAmounts[pid]?.[line.key]
            ),
          }));
          const result = await createBill(
            order?._id,
            partyId,
            subTotal,
            duePrintingAmount,
            dueMountingAmount,
            dueAmountInvWise
          );

          // Handle notification only based on the result of each createBill call
          if (result.success) {
            notificationMessage =
              "Bills sent to billing successfully for selected parties";
          } else {
            notification.error({
              message: "Error occurred while sending to billing",
              description: result.message,
            });
            return; // Stop further execution if an error occurs
          }
        }
        // Show success notification after all calls are successful
        notification.success({ message: notificationMessage });
        fetchSalesOrders("completed");
        handleCloseBillingModal();
      } else {
        // If splitBill is false, proceed with the normal createBill logic
        const result = await createBill(order?._id);
        if (result.success) {
          notification.success({
            message: "Sales Order sent to billing successfully",
          });
          fetchSalesOrders("completed");
          handleCloseBillingModal();
        } else {
          notification.error({
            message: "Error occurred while sending to billing",
            description: result.message,
          });
        }
      }
    } catch (error) {
      notification.error({
        message: "Error occurred while sending to billing",
        description: error.message,
      });
    } finally {
      setIsCreatingBill(false);
    }
  };

  const tradingInventories =
    order?.bdfId?.inventories?.filter(
      (inventory) => inventory?.invId?.isTrading
    ) || [];

  const isPOEnabled = tradingInventories && tradingInventories?.length > 0;

  // Get common start and end dates from inventories
  let commonStartDate = null;
  let commonEndDate = null;

  if (Array.isArray(order?.bdfId?.inventories)) {
    const startDates = order.bdfId.inventories
      .map((inv) => moment(inv.startDate))
      .filter((d) => d.isValid());
    const endDates = order.bdfId.inventories
      .map((inv) => moment(inv.endDate))
      .filter((d) => d.isValid());

    if (startDates.length && endDates.length) {
      commonStartDate = moment.min(startDates).format("DD/MM/YYYY");
      commonEndDate = moment.max(endDates).format("DD/MM/YYYY");
    }
  }
  const sendCreditAmount = async (billNumber, amount) => {
    try {
    } catch (error) {
      notification.error({ message: "Error while sending credit amount" });
    }
  };

  const fetchBillNumbers = async () => {
    try {
      setLoadingBillNumbers(true);
      setCreditNoteTableData([]);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/getBillNumbersBySalesOrderId?salesOrderId=${order._id}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
        }
      );
      const data = await response.json();

      if (data.success && data.invoices) {
        setBillNumbers(data.invoices);
      } else {
        notification.warning({
          message: "No Bills Found",
          description: data.message || "No bills found for this sales order",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch bill numbers",
      });
    } finally {
      setLoadingBillNumbers(false);
    }
  };

  const handleOpenCreditNoteModal = () => {
    setIsCreditNoteModalOpen(true);
    fetchBillNumbers(); // Fetch bill numbers when modal opens
  };

  const handleCloseCreditNoteModal = () => {
    setSelectedBillNumber(null);
    setCreditNoteAmount();
    setCreditNoteTableData([]);
    setCreditNoteCharges({ printingCharge: 0, mountingCharge: 0 });
    setGenerateFullInvoice(false);
    setIsCreditNoteModalOpen(false);
  };

  const handleCreditNoteSubmit = async () => {
    setIsCreditNoteSubmitting(true);
    try {
      console.log("creditNoteTableData from api", creditNoteTableData);

      const invAgainst = creditNoteTableData.map((item) => {
        return {
          invId: item.invId,
          amount: item.creditNoteAmount,
          startDate: toCreditNoteApiDate(item.startDate),
          endDate: toCreditNoteApiDate(item.endDate),
        };
      });

      // Fix: totalAmount should be sum of Credit Note Amounts
      const totalAmountForCN = creditNoteAmountSum;

      const infoForCN = {
        totalAmount: totalAmountForCN,
        inventoryAgainst: invAgainst,
        printingCharge: creditNoteCharges?.printingCharge,
        mountingCharge: creditNoteCharges?.mountingCharge,
      };

      const requestedBody = {
        invoiceId,
        infoForCN,
        isCNGeneratedAgainstFullInvoice: Boolean(generateFullInvoice),
      };
      console.log("tbhis req body", requestedBody);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/createInfoForCN`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${token}`,
          },
          body: JSON.stringify(requestedBody),
        }
      );

      const raw = await response.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (parseErr) {
        console.error("createInfoForCN: invalid JSON body", parseErr, raw);
        throw new Error(
          response.ok
            ? "Server returned an invalid response. Please try again."
            : `Server error (${response.status}). Please try again.`
        );
      }

      const succeeded =
        data.success === true || data.success === "true" || data.success === 1;

      if (!response.ok) {
        notification.error({
          message: "Failed to send credit note to billing",
          description:
            data.message ||
            `Request failed (${response.status}). Please try again.`,
        });
        return;
      }

      if (succeeded) {
        notification.success({
          message: "Credit note alert sent successfully",
          description: data.message || undefined,
          duration: 4.5,
          placement: "topRight",
        });
        handleCloseCreditNoteModal();
      } else {
        notification.error({
          message: "Failed to send credit note to billing",
          description:
            data.message || "Something went wrong. Please try again.",
        });
      }
    } catch (error) {
      console.error("createInfoForCN:", error);
      notification.error({
        message: "Failed to send credit note to billing",
        description:
          error?.message || "Network error or unexpected response. Try again.",
      });
    }
    finally {
      setIsCreditNoteSubmitting(false);
    }
  };
  const handleCreditNoteAmountChange = (key, value) => {
    setCreditNoteTableData((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, creditNoteAmount: value } : row
      )
    );
    // No need to manually set remainingAmount, handled by derived state
  };

  const handleTotalCreditNoteChange = (value) => {
    setCreditNoteAmount(value);
    // No need to manually set remainingAmount, handled by derived state
  };

  const handleGenerateFullInvoiceToggle = (checked) => {
    setGenerateFullInvoice(checked);
    if (checked) {
      setCreditNoteTableData((prev) =>
        prev.map((row) => ({ ...row, creditNoteAmount: row.amount }))
      );
      const tot = creditNoteTableData.reduce((acc, item) => {
        return acc + item.amount;
      }, 0);
      setCreditNoteAmount(tot);
    } else {
      setCreditNoteTableData((prev) =>
        prev.map((row) => ({ ...row, creditNoteAmount: null }))
      );
      setCreditNoteAmount(0);
    }
  };

  const handleDateChange = (date, field, rowKey) => {
    const formatted = date ? toCreditNoteApiDate(date) : null;
    setCreditNoteTableData((prev) =>
      prev.map((row) =>
        row.key === rowKey ? { ...row, [field]: formatted } : row
      )
    );
  };

  const creditNoteColumns = [
    { title: "Code No", dataIndex: "codeNo", key: "codeNo" },
    { title: "Location", dataIndex: "location", key: "location" },
    { title: "Size", dataIndex: "size", key: "size", width: 80 },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      width: 150,
      render: (date, record) => (
        <DatePicker
          value={
            date
              ? dayjs(date, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid()
                ? dayjs(date, ["YYYY-MM-DD", "DD-MM-YYYY"])
                : dayjs(date)
              : null
          }
          format="DD-MM-YYYY"
          onChange={(newDate) =>
            handleDateChange(newDate, "startDate", record.key)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: 150,
      render: (date, record) => (
        <DatePicker
          value={
            date
              ? dayjs(date, ["YYYY-MM-DD", "DD-MM-YYYY"], true).isValid()
                ? dayjs(date, ["YYYY-MM-DD", "DD-MM-YYYY"])
                : dayjs(date)
              : null
          }
          format="DD-MM-YYYY"
          onChange={(newDate) =>
            handleDateChange(newDate, "endDate", record.key)
          }
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value) => value.toLocaleString(),
    },
    {
      title: "Credit Note Amount",
      dataIndex: "creditNoteAmount",
      key: "creditNoteAmount",
      render: (value, record) => (
        <InputNumber
          min={0}
          style={{ width: "100%" }}
          value={value}
          onChange={(val) => handleCreditNoteAmountChange(record.key, val)}
          formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(val) => val.replace(/(,*)/g, "")}
          disabled={generateFullInvoice}
        />
      ),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  /** Split-bill inventory table: centered header labels in cell */
  const splitBillTableComponents = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            textAlign: "center",
            verticalAlign: "middle",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="salesOrder-list fileInput first-prop"
      style={{
        backgroundColor: order?.isRevisedBDF ? "#fff0f0" : "white",
      }}
    >
      {order?.isRevisedBDF && (
        <div
          style={{
            color: "red",
            position: "absolute",
            top: "0.2rem",
            left: "2rem",
            fontSize: "0.8rem",
          }}
        >
          Revised BDF
        </div>
      )}
      <div className="salesOrder-date">
        <div className="salesOrder-date-child">
          <span>{moment(order?.createdAt).format("DD")}</span> {/* Day */}
          <span>{moment(order?.createdAt).format("MMM")}</span> {/* Month */}
          <span>{moment(order?.createdAt).format("YYYY")}</span> {/* Year */}
        </div>
        <span className="date-vertical-border"></span>
      </div>
      <div>
        <div style={{ whiteSpace: "nowrap", color: "black" }}>
          {order?.stamp ? "SO" : "BDF Serial No."} : <span style={{ color: "grey" }}>{order?.stamp || order?.bdfId?.stamp}</span>
          {type === "all" && (
            <Tag
              color={order?.SOConfirmed ? "success" : "processing"}
              style={{ marginLeft: "10px" }}
            >
              {order?.SOConfirmed ? "Completed" : "Pending"}
            </Tag>
          )}
        </div>
        {/* ID */}
        <div className="salesOrder-meta-info">
          Sales Person: {order?.bdfId?.salesPerson}
        </div>{" "}
        {/* Approved By */}
        <div className="salesOrder-meta-info">
          Created By: {order?.bdfId?.createdBy?.name}
        </div>{" "}
        {/* Created By */}
      </div>
      <div className="salesOrder-info-dt">
        <div className="salesOrder-info-dt-child">
          <span>Amount :</span>
          <div>₹ {formatIndianNumber(totalAmount)}</div>
        </div>

        <div className="salesOrder-info-dt-child">
          <span>Inventory Count :</span>

          <div>({order?.bdfId?.inventories?.length})</div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div>
          Party Name:{" "}
          <span style={{ color: "grey" }}>
            {order?.bdfId?.parentCompanyId?.name || order?.bdfId?.parentCompanyName}
          </span>
        </div>
        {type === "billed" &&
          Array.isArray(order?.splittedParties) &&
          order.splittedParties.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div>Splitted Parties:</div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "4px 6px",
                }}
              >
                {order.splittedParties.map((party, idx) => (
                  <Tooltip
                    key={party.billingId || idx}
                    title={
                      party.gstNumber
                        ? `GST: ${party.gstNumber}`
                        : party.name
                    }
                  >
                    <Tag
                      color="geekblue"
                      style={{
                        margin: 0,
                        fontSize: 11,
                        lineHeight: "18px",
                        padding: "0 6px",
                        maxWidth: 260,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {party.name}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}
        <div>
          Brand Name:{" "}
          <span style={{ color: "grey" }}>
            {order?.bdfId?.brandId?.brandName?.toUpperCase()}
          </span>
        </div>
        <div>
          Campaign Name:{" "}
          <span style={{ color: "grey" }}>
            {order?.bdfId?.campaignName?.toUpperCase()}
          </span>
        </div>
      </div>

      {order?.isFromSub ? (
        order?.bdfId?.bdfDate ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div>
              Start Date:{" "}
              <span style={{ color: "grey" }}>
                {String(order?.bdfId?.bdfDate).split("-").join("/")}
              </span>
            </div>
            <div>
              End Date:{" "}
              <span style={{ color: "grey" }}>
                {String(order?.bdfId?.bdfDate).split("-").join("/")}
              </span>
            </div>
          </div>
        ) : null
      ) : (
        commonStartDate &&
        commonEndDate && (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
          >
            <div>
              Start Date:{" "}
              <span style={{ color: "grey" }}>{commonStartDate}</span>
            </div>
            <div>
              End Date: <span style={{ color: "grey" }}>{commonEndDate}</span>
            </div>
          </div>
        )
      )}

      {(order?.SOConfirmed || type === "all") && (
        <>
          <div
            style={{ color: "#1677ff", cursor: "pointer" }}
            onClick={() => {
              navigateToDetails(order?._id, true);
            }}
          >
            View
          </div>

          {/* {type !== "all" && type !== "billed" && ( */}
          <div
            style={{ color: "#1677ff", cursor: "pointer" }}
            onClick={() => {
              navigateToDetails(order?._id, false);
            }}
          >
            Edit
          </div>

          {/* )} */}


        </>
      )}

      {/* Show Delete on all tabs, except when there is any billingInfo */}
      {!order?.billingInfo?.length && (
        <>
          <div
            style={{ color: "red", cursor: "pointer" }}
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete
          </div>
          <Modal
            open={deleteModalOpen}
            onOk={() => {
              if (!deleteReason?.trim()) {
                message.error("Delete reason is required");
                return;
              }
              handleArchive(deleteReason);
            }}
            onCancel={() => {
              setDeleteModalOpen(false);
              setDeleteReason("");
            }}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ disabled: !deleteReason?.trim() }}
            destroyOnClose
          >
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              Delete reason <span style={{ color: "red" }}>*</span>
            </div>
            <Input.TextArea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter delete reason (required)"
              rows={3}
              required
            />
          </Modal>
        </>
      )}

      {/* Billing info view trigger, same as in Billing List */}
      {order?.billingInfo?.length > 0 && (
        <div
          style={{ color: "#1677ff", cursor: "pointer" }}
          onClick={() => setViewBillsModalOpen(true)}
        >
          Billing Info
        </div>
      )}
      {order?.cnInfo?.length > 0 && (
        <div
          style={{ color: "#1677ff", cursor: "pointer" }}
          onClick={() => setViewCnInfoModalOpen(true)}
        >
          CN Info
        </div>
      )}
      {/* 
      <Button
        type="default"
        onClick={async () => {
          const normalizeOwnedBy = (ownedBy) => {
            if (!ownedBy) return "unknown";
            return ownedBy.trim().toLowerCase().replace(/\s+/g, "");
          };

          // Grouping inventories based on `ownedBy`
          const groupedInventories = tradingInventories.reduce(
            (acc, inventory) => {
              const ownedByNormalized = normalizeOwnedBy(
                inventory.invId?.ownedBy
              );

              if (!acc[ownedByNormalized]) {
                acc[ownedByNormalized] = [];
              }
              acc[ownedByNormalized].push(inventory);
              return acc;
            },
            {}
          );

          // Ensure order.poNumber exists and matches the grouped inventories count
          const poNumbers = order?.poNumber || [];
          const ownedByKeys = Object.keys(groupedInventories);

          if (poNumbers.length !== ownedByKeys.length) {
            console.warn(
              "Mismatch between PO numbers and ownedBy groups. Verify data integrity."
            );
          }

          try {
            await Promise.all(
              ownedByKeys.map(async (ownedBy, index) => {
                const poNumber = poNumbers[index] || "-";
                await generatePurchaseOrderPDF(
                  groupedInventories[ownedBy],
                  order?.bdfId,
                  poNumber
                );
              })
            );
          } catch (error) {
            console.error("Error generating PDFs:", error);
          }
        }}
        disabled={!isPOEnabled}
      >
        PO
      </Button> */}

      {(order?.SOConfirmed || type === "all" || type === "pending") && (
        <Button type="default" onClick={handleOpenModal}>
          PI
        </Button>
      )}

      <div>
        <GeneratePerformanceInvoicePDF
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          order={order}
        />
      </div>

      {/* Billing Info modal, reusing same structure as Billing List */}
      <Modal
        title="Bills"
        open={viewBillsModalOpen}
        onCancel={() => setViewBillsModalOpen(false)}
        footer={null}
        width={600}
      >
        <Table
          dataSource={order?.billingInfo || []}
          rowKey={(_, index) => index}
          pagination={false}
          size="small"
          components={components}
          columns={[
            {
              title: "Billing Date",
              dataIndex: "billingDate",
              key: "billingDate",
              align: "center",
              render: (date) =>
                date ? moment(date).format("DD/MM/YYYY") : "—",
            },
            {
              title: "Bill Number",
              dataIndex: "billNumber",
              key: "billNumber",
              align: "center",
            },
            {
              title: "Grand Total",
              dataIndex: "grandTotal",
              key: "grandTotal",
              align: "center",
              render: (val) =>
                val != null ? `₹ ${formatIndianNumber(val)}` : "—",
            },
          ]}
        />
      </Modal>

      <Modal
        title="Credit Notes"
        open={viewCnInfoModalOpen}
        onCancel={() => setViewCnInfoModalOpen(false)}
        footer={null}
        width={600}
      >
        <Table
          dataSource={order?.cnInfo || []}
          rowKey={(_, index) => `cn-${index}`}
          pagination={false}
          size="small"
          components={components}
          columns={[
            {
              title: "CN Date",
              dataIndex: "cnDate",
              key: "cnDate",
              align: "center",
              render: (date) =>
                date ? moment(date).format("DD/MM/YYYY") : "—",
            },
            {
              title: "CN Number",
              dataIndex: "cnNumber",
              key: "cnNumber",
              align: "center",
            },
            {
              title: "CN Amount",
              dataIndex: "cnAmount",
              key: "cnAmount",
              align: "center",
              render: (val) =>
                val != null ? `₹ ${formatIndianNumber(val)}` : "—",
            },
          ]}
        />
      </Modal>

      {type === "pending" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
            marginLeft: "auto",
          }}
        >
          <Button
            type="primary"
            onClick={() => {
              navigateToDetails(order?._id, false);
            }}
          >
            Create Sales Order
          </Button>
        </div>
      )}
      {order?.SOConfirmed && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
            marginLeft: "auto",
          }}
        >
          {type === "billed" ? (
            <Button type="primary" onClick={handleOpenCreditNoteModal}>
              Send for Credit Note
            </Button>
          ) : type !== "all" ? (
            <Button type="primary" onClick={handleOpenBillingModal}>
              Send to Billing
            </Button>
          ) : null}
        </div>
      )}

      {/* Billing Modal */}
      <Modal
        title={<Title level={4}>Send to Billing</Title>}
        open={isBillingModalOpen}
        onOk={handleCreateBill}
        onCancel={handleCloseBillingModal}
        okText="Confirm"
        cancelText="Cancel"
        width={1000}
        centered
        okButtonProps={{
          disabled:
            (splitBill && !isSplitAllocationBalanced) || isCreatingBill,
          loading: isCreatingBill,
        }}
      >
        <div style={{ padding: "20px 0" }}>
          <div style={{ marginBottom: "20px" }}>
            <Switch
              checked={splitBill}
              onChange={(checked) => {
                setSplitBill(checked);
                if (checked) {
                  setSelectedParties([]);
                  setPartySplitFields({});
                  setPartyInvDueAmounts({});
                }
              }}
              checkedChildren="Create bill with split"
              unCheckedChildren="Create bill without split"
            />
          </div>

          <Divider style={{ margin: "15px 0" }} />

          <div style={{ marginBottom: "20px" }}>
            <Text
              strong
              style={{
                fontSize: 18,
                lineHeight: 1.5,
                display: "block",
              }}
            >
              Total Bill Amount: ₹ {formatIndianNumber(totalAmount)} (Special ₹
              {formatIndianNumber(splitBillTotals.specialRate)} · Printing ₹
              {formatIndianNumber(splitBillTotals.printingCharge)} · Mounting ₹
              {formatIndianNumber(splitBillTotals.mountingCharge)})
            </Text>
          </div>

          {!splitBill && (
            <div style={{ marginBottom: "20px" }}>
              <Alert
                message="Single Billing Confirmation"
                description={
                  <>
                    <p>
                      You are about to create a single bill for the full amount
                      of ₹ {formatIndianNumber(totalAmount)}.
                    </p>
                    <p>
                      Click "Confirm" to proceed or toggle the switch above to
                      split this bill among multiple parties.
                    </p>
                  </>
                }
                type="info"
                showIcon
              />
            </div>
          )}

          {splitBill && (
            <div>
              <div style={{ marginBottom: "20px" }}>
                <Row gutter={16}>
                  <Col span={24}>
                    <Select
                      mode="multiple"
                      placeholder="Select companies to split the bill"
                      style={{ width: "100%" }}
                      value={selectedParties.map(String)}
                      onChange={(ids) =>
                        setSelectedParties((ids || []).map(String))
                      }
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const party = allParties.find(
                          (p) => String(p._id) === String(option.value)
                        );

                        if (!party) return false;

                        // Create a searchable string from party name and GST number
                        const searchText = `${party.name} ${party.gstNumber || ""
                          }`.toLowerCase();

                        // Check if the input is included in the searchable string
                        return searchText.includes(input.toLowerCase());
                      }}
                    >
                      {allParties.map((party) => (
                        <Option key={String(party._id)} value={String(party._id)}>
                          <strong>{party.name}</strong>
                          {party.gstNumber ? ` (GST: ${party.gstNumber})` : ""}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              </div>

              {selectedParties.length > 0 && (
                <>
                  <div
                    style={{
                      marginBottom: 16,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button type="primary" onClick={distributeBillEvenly}>
                      Distribute evenly
                    </Button>
                  </div>

                  <Collapse
                    key={selectedParties.map(String).join("|")}
                    style={{ marginBottom: 12 }}
                    defaultActiveKey={selectedParties.map(String)}
                    items={selectedParties.map((partyId) => {
                      const pid = String(partyId);
                      const party = allParties.find(
                        (p) => String(p._id) === pid
                      );
                      const fields = partySplitFields[pid] || {};
                      const chargeInputProps = {
                        addonBefore: "₹",
                        style: { width: "100%" },
                        min: 0,
                        precision: 2,
                        step: 1,
                        formatter: (value) =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                        parser: (value) => value.replace(/\₹\s?|(,*)/g, ""),
                      };
                      const dueInputProps = {
                        addonBefore: "₹",
                        style: { width: "100%" },
                        min: 0,
                        precision: 2,
                        step: 1,
                        formatter: (value) =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                        parser: (value) => value.replace(/\₹\s?|(,*)/g, ""),
                      };
                      return {
                        key: pid,
                        label: (
                          <Space wrap>
                            <Text strong>
                              {party?.name || "Unknown Party"}
                            </Text>
                            <Text type="secondary">
                              Special rate ₹
                              {formatIndianNumber(partySpecialRate(pid))} ·
                              Printing ₹
                              {formatIndianNumber(fields.printingCharge || 0)} ·
                              Mounting ₹
                              {formatIndianNumber(fields.mountingCharge || 0)}
                            </Text>
                          </Space>
                        ),
                        children: (
                          <div>
                            <Table
                              size="small"
                              pagination={false}
                              scroll={{ x: "max-content" }}
                              rowKey={(row) => row.key}
                              dataSource={splitInventoryLines}
                              components={splitBillTableComponents}
                              columns={[
                                {
                                  title: "Code No.",
                                  dataIndex: "codeNo",
                                  key: "codeNo",
                                  width: 100,
                                  align: "center",
                                },
                                {
                                  title: "Location",
                                  dataIndex: "locationName",
                                  key: "locationName",
                                  ellipsis: true,
                                  align: "center",
                                },
                                {
                                  title: "Special Rate",
                                  key: "specialRateCol",
                                  width: 160,
                                  align: "center",
                                  render: (_, line) => (
                                    <InputNumber
                                      {...dueInputProps}
                                      max={line.lineSpecialTotal}
                                      value={normalizeAmount(
                                        partyInvDueAmounts[pid]?.[line.key]
                                      )}
                                      onChange={(value) =>
                                        handleInvDueChange(
                                          pid,
                                          line.key,
                                          value
                                        )
                                      }
                                    />
                                  ),
                                },
                              ]}
                            />
                            <Row gutter={12} style={{ marginTop: 16 }}>
                              <Col xs={24} sm={12}>
                                <Text
                                  type="secondary"
                                  style={{
                                    display: "block",
                                    marginBottom: 4,
                                    fontSize: 12,
                                  }}
                                >
                                  Printing charge
                                </Text>
                                <InputNumber
                                  {...chargeInputProps}
                                  max={splitBillTotals.printingCharge}
                                  value={normalizeAmount(fields.printingCharge)}
                                  onChange={(value) =>
                                    handleSplitFieldChange(
                                      pid,
                                      "printingCharge",
                                      value
                                    )
                                  }
                                />
                              </Col>
                              <Col xs={24} sm={12}>
                                <Text
                                  type="secondary"
                                  style={{
                                    display: "block",
                                    marginBottom: 4,
                                    fontSize: 12,
                                  }}
                                >
                                  Mounting charge
                                </Text>
                                <InputNumber
                                  {...chargeInputProps}
                                  max={splitBillTotals.mountingCharge}
                                  value={normalizeAmount(fields.mountingCharge)}
                                  onChange={(value) =>
                                    handleSplitFieldChange(
                                      pid,
                                      "mountingCharge",
                                      value
                                    )
                                  }
                                />
                              </Col>
                            </Row>
                          </div>
                        ),
                      };
                    })}
                  />

                  {!aggregateSplitBalanced && (
                    <Alert
                      message="Amounts remaining to be distributed:"
                      description={
                        <Text
                          strong
                          style={{ fontSize: 17, lineHeight: 1.6 }}
                        >
                          Special Rate: ₹
                          {formatIndianNumber(remainingSpecialRate)} ·
                          Printing Charge: ₹
                          {formatIndianNumber(remainingPrintingCharge)} ·
                          Mounting Charge: ₹
                          {formatIndianNumber(remainingMountingCharge)}
                        </Text>
                      }
                      type={
                        remainingPrintingCharge < 0 ||
                          remainingMountingCharge < 0 ||
                          remainingSpecialRate < 0
                          ? "error"
                          : "warning"
                      }
                      showIcon
                      style={{ marginTop: "15px" }}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Credit Note Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "bold",
            }}
          >
            <span>Credit Note</span>
          </div>
        }
        open={isCreditNoteModalOpen}
        onOk={handleCreditNoteSubmit}
        onCancel={handleCloseCreditNoteModal}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={isCreditNoteSubmitting}
        width={900}
        centered
      >
        <div style={{ padding: "20px 0" }}>
          <div style={{ marginBottom: "20px" }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Title level={5}>Credit Note Details</Title>
              </Col>
              <Col span={24}>
                <Select
                  placeholder="Select Bill Number"
                  style={{ width: "100%" }}
                  value={selectedBillNumber}
                  onChange={(value) => {
                    setInvoiceId(value);
                    setSelectedBillNumber(value);
                    fetchInvoicDataForCN(value);
                  }}
                  loading={loadingBillNumbers}
                >
                  {billNumbers.map((invoice) => (
                    <Option
                      key={invoice._id}
                      value={invoice._id}
                      disabled={invoice.creditNoteAlert}
                      style={{
                        backgroundColor: invoice.creditNoteAlert
                          ? "#f26161"
                          : "",
                        color: invoice.creditNoteAlert ? "black" : "black",
                      }}
                    >
                      {invoice.billNumber}
                      {invoice.creditNoteAlert ? (
                        <>
                          <p>Credit note already sent</p>
                        </>
                      ) : (
                        " "
                      )}
                    </Option>
                  ))}
                </Select>
              </Col>
              {/* <Col span={24}>
                {(creditNoteAmountSum > 0 || creditNoteCharges?.printingCharge > 0 || creditNoteCharges?.mountingCharge > 0) && (
                  <Alert
                    message={
                      remainingToDistribute === 0
                        ? "Amount distributed successfully!"
                        : `Amount remaining to be distributed: ₹${formatIndianNumber(remainingToDistribute)}`
                    }
                    type={
                      remainingToDistribute === 0
                        ? "success"
                        : remainingToDistribute > 0
                          ? "info"
                          : "error"
                    }
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Col> */}

            </Row>
          </div>
          {/* Table for inventory/credit note details */}
          {selectedBillNumber && (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 8,
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  Generate against full invoice
                </span>
                <Switch
                  checked={generateFullInvoice}
                  onChange={handleGenerateFullInvoiceToggle}
                  disabled={!selectedBillNumber}
                />
              </div>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                  <span>
                    <strong>Note:</strong> If you want to generate the Credit
                    Note against the <strong>full invoice</strong>, please
                    enable <strong>“Generate against full invoice”</strong>{" "}
                    above.
                  </span>
                }
              />
              <Table
                dataSource={creditNoteTableData}
                columns={creditNoteColumns}
                components={components}
                pagination={false}
                rowKey="key"
                style={{ marginTop: 16 }}
              />
              <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                <Col span={6}>
                  <Text strong>Amount:</Text>
                  <InputNumber
                    addonBefore="₹"
                    style={{ width: "100%" }}
                    min={0}
                    value={creditNoteAmountSum}
                    disabled
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={value => value.replace(/\₹\s?|(,*)/g, "")}
                    placeholder="Amount"
                  />
                </Col>
                <Col span={6}>
                  <Text strong>Printing Charge:</Text>
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={creditNoteCharges?.printingCharge || 0}
                    onChange={val => setCreditNoteCharges(charges => ({ ...charges, printingCharge: val || 0 }))}
                    disabled={generateFullInvoice}
                  />
                </Col>
                <Col span={6}>
                  <Text strong>Mounting Charge:</Text>
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    value={creditNoteCharges?.mountingCharge || 0}
                    onChange={val => setCreditNoteCharges(charges => ({ ...charges, mountingCharge: val || 0 }))}
                    disabled={generateFullInvoice}
                  />
                </Col>
                <Col span={6}>
                  <Text strong>Total:</Text>
                  <div style={{ fontWeight: 600, fontSize: 16, marginTop: 4 }}>
                    ₹ {formatIndianNumber(creditNoteTotal)}
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
export default List;
